import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, UserCog } from 'lucide-react'

import { patchMember, type ProductionDepartment, type ProductionMember } from '@/api/studio-production'
import { MEMBER_ROLES, type MemberRole } from '@/features/studio/lib/production-constants'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { memberDisplayName } from '@/features/studio/modals/InviteMemberModal'
import {
  ProtoBtn,
  ProtoLabel,
  ProtoModal,
  ProtoSelect,
} from '@/features/studio/components/ui-kit'

export function EditMemberModal({
  open,
  onClose,
  productionId,
  member,
  departments,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  member: ProductionMember | null
  departments: ProductionDepartment[]
  onSaved?: () => void
}) {
  const { t } = useTranslation()
  const [role, setRole] = useState<MemberRole>('contributor')
  const [departmentId, setDepartmentId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!member) return
    setRole((member.role as MemberRole) || 'contributor')
    setDepartmentId(member.department_id ?? '')
    setError(null)
  }, [member])

  async function handleSave() {
    if (!member) return
    setSubmitting(true)
    setError(null)
    try {
      await patchMember(productionId, member.user_id, {
        role,
        department_id: departmentId || null,
      })
      onSaved?.()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.patchMemberFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('team.editMemberTitle')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" onClick={() => void handleSave()} disabled={!member || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />}
            {submitting ? t('settings.save') + '…' : t('settings.save')}
          </ProtoBtn>
        </>
      }
    >
      {member ? (
        <p className="mb-4 text-sm text-zinc-400">
          {memberDisplayName(member)}
          <span className="ml-2 text-[11px] text-zinc-600">#{member.user_id}</span>
        </p>
      ) : null}
      {error ? <p className="mb-3 text-xs text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <ProtoLabel>{t('team.fieldRole')}</ProtoLabel>
          <ProtoSelect value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`production.roles.${r}`, { defaultValue: r })}
              </option>
            ))}
          </ProtoSelect>
        </div>
        <div>
          <ProtoLabel>{t('team.fieldDepartment')}</ProtoLabel>
          <ProtoSelect value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">{t('team.noDepartment')}</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </ProtoSelect>
        </div>
      </div>
    </ProtoModal>
  )
}
